import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { toast } from '@/components/ui/use-toast';
import { getUmpire, updateUmpire, uploadProfilePicture, uploadCoverImage } from '@/services/supabaseService';
import { Umpire } from '@/integrations/supabase/types';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Avatar } from '@/components/ui/avatar';
import { X, Pencil } from 'lucide-react';
import { Slider } from '@/components/ui/slider';

interface EditUmpireModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  umpireId: string | null;
}

const EditUmpireModal: React.FC<EditUmpireModalProps> = ({ open, onOpenChange, umpireId }) => {
  const [umpire, setUmpire] = useState<Partial<Umpire>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const [profilePicturePreviewUrl, setProfilePicturePreviewUrl] = useState<string | null>(null);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreviewUrl, setCoverImagePreviewUrl] = useState<string | null>(null);
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [croppingFor, setCroppingFor] = useState<'profile' | 'cover' | null>(null);
  const profilePicFileInputRef = React.useRef<HTMLInputElement>(null);
  const coverImageFileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadUmpire = async () => {
      try {
        if (!umpireId) return;
        const data = await getUmpire(umpireId);
        setUmpire(data || {});
      } catch (err: any) {
        setError(err.message || 'Failed to load umpire');
      }
    };
    if (open && umpireId) loadUmpire();
  }, [umpireId, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setUmpire((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUmpire((prev) => ({ ...prev, [name]: value === '' ? undefined : Number(value) }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: 'profile' | 'cover') => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (type === 'profile') {
        setProfilePictureFile(file);
        setProfilePicturePreviewUrl(URL.createObjectURL(file));
      } else {
        setCoverImageFile(file);
        setCoverImagePreviewUrl(URL.createObjectURL(file));
      }
      setCroppingFor(type);
      setIsCropModalOpen(true);
    }
  };

  const handleCropConfirm = React.useCallback(async () => {
    if (croppingFor === 'profile' && profilePicturePreviewUrl) {
      setUmpire(prev => ({ ...prev, profilePhoto: profilePicturePreviewUrl }));
    } else if (croppingFor === 'cover' && coverImagePreviewUrl) {
      setCoverImage(coverImagePreviewUrl);
      setUmpire(prev => ({ ...prev, coverImage: coverImagePreviewUrl }));
    }
    setIsCropModalOpen(false);
  }, [croppingFor, profilePicturePreviewUrl, coverImagePreviewUrl]);

  const handleRemoveProfilePic = () => {
    setProfilePictureFile(null);
    setProfilePicturePreviewUrl(null);
    setUmpire(prev => ({ ...prev, profilePhoto: '' }));
  };
  const handleRemoveCoverImage = () => {
    setCoverImageFile(null);
    setCoverImagePreviewUrl(null);
    setCoverImage(null);
    setUmpire(prev => ({ ...prev, coverImage: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    if (!umpire.firstName || !umpire.lastName) {
      setError('First name and last name are required.');
      setIsSubmitting(false);
      return;
    }
    try {
      let profilePhotoUrl = umpire.profilePhoto;
      let coverImageUrl = coverImage;
      if (profilePictureFile) {
        profilePhotoUrl = await uploadProfilePicture(profilePictureFile);
      }
      if (coverImageFile) {
        coverImageUrl = await uploadCoverImage(coverImageFile);
      }
      await updateUmpire(umpireId!, { ...umpire, profilePhoto: profilePhotoUrl, coverImage: coverImageUrl });
      toast({ title: 'Success', description: `Umpire ${umpire.firstName} ${umpire.lastName} updated successfully!` });
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update umpire');
      toast({ title: 'Error', description: err.message || 'Failed to update umpire', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-full p-0">
        <div className="py-8 px-4 sm:px-8 animate-fade-in">
          <Card className="shadow-none border-none">
            <CardHeader>
              <CardTitle>Edit Umpire</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input id="lastName" name="lastName" value={umpire.lastName || ''} onChange={handleChange} required />
                  </div>
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input id="firstName" name="firstName" value={umpire.firstName || ''} onChange={handleChange} required />
                  </div>
                  <div>
                    <Label htmlFor="middleName">Middle Name</Label>
                    <Input id="middleName" name="middleName" value={umpire.middleName || ''} onChange={handleChange} />
                  </div>
                </div>

                {/* Profile & Cover Image Upload - two column grid, labels above, icon placeholder, clickable area */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-6 items-center">
                  <div className="flex flex-col items-center gap-2">
                    <Label className="mb-2">Profile Picture</Label>
                    <div
                      className="w-24 h-24 rounded-full bg-gray-500 flex items-center justify-center overflow-hidden cursor-pointer group transition-all duration-150 hover:ring-2 hover:ring-primary relative"
                      onClick={() => profilePicFileInputRef.current?.click()}
                      title="Change profile picture"
                    >
                      {umpire.profilePhoto ? (
                        <img src={umpire.profilePhoto} alt="Profile" className="w-full h-full object-cover rounded-full" />
                      ) : null}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 group-hover:bg-black/60 transition-all duration-150">
                        <svg className="w-10 h-10 text-gray-200" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a4 4 0 01-1.414.828l-4.243 1.414 1.414-4.243a4 4 0 01.828-1.414z" /></svg>
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      ref={profilePicFileInputRef}
                      className="hidden"
                      onChange={e => handleFileChange(e, 'profile')}
                    />
                    {umpire.profilePhoto && (
                      <Button type="button" variant="ghost" size="sm" onClick={handleRemoveProfilePic}>Remove</Button>
                    )}
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <Label className="mb-2">Cover Image</Label>
                    <div
                      className="w-full max-w-md h-24 rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden cursor-pointer group transition-all duration-150 hover:ring-2 hover:ring-primary relative"
                      onClick={() => coverImageFileInputRef.current?.click()}
                      title="Change cover image"
                    >
                      {coverImage ? (
                        <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />
                      ) : null}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-all duration-150">
                        <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 17l6-6a2 2 0 012.83 0l4.34 4.34a2 2 0 002.83 0L21 13" /></svg>
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      ref={coverImageFileInputRef}
                      className="hidden"
                      onChange={e => handleFileChange(e, 'cover')}
                    />
                    {coverImage && (
                      <Button type="button" variant="ghost" size="sm" onClick={handleRemoveCoverImage}>Remove</Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="gender">Gender *</Label>
                    <Select name="gender" value={umpire.gender || ''} onValueChange={val => setUmpire(prev => ({ ...prev, gender: val }))} required>
                      <SelectTrigger id="gender">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                        <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="status">Status *</Label>
                    <Select name="status" value={umpire.status || ''} onValueChange={val => setUmpire(prev => ({ ...prev, status: val }))} required>
                      <SelectTrigger id="status">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Retired">Retired</SelectItem>
                        <SelectItem value="Suspended">Suspended</SelectItem>
                        <SelectItem value="Deceased">Deceased</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="availabilityStatus">Availability</Label>
                    <Select name="availabilityStatus" value={umpire.availabilityStatus || ''} onValueChange={val => setUmpire(prev => ({ ...prev, availabilityStatus: val }))}>
                      <SelectTrigger id="availabilityStatus">
                        <SelectValue placeholder="Select availability" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Available">Available</SelectItem>
                        <SelectItem value="Unavailable">Unavailable</SelectItem>
                        <SelectItem value="On Leave">On Leave</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Accordion type="multiple" className="mt-6">
                  <AccordionItem value="personal-details">
                    <AccordionTrigger className="text-left font-semibold px-0 py-4 hover:bg-transparent focus:bg-transparent focus:outline-none">
                      Personal Details
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="dateOfBirth">Date of Birth</Label>
                          <Input id="dateOfBirth" name="dateOfBirth" type="date" value={umpire.dateOfBirth || ''} onChange={handleChange} />
                        </div>
                        <div>
                          <Label htmlFor="nationality">Nationality</Label>
                          <Input id="nationality" name="nationality" value={umpire.nationality || ''} onChange={handleChange} />
                        </div>
                        <div>
                          <Label htmlFor="debutYear">Debut Year</Label>
                          <Input id="debutYear" name="debutYear" type="number" value={umpire.debutYear ?? ''} onChange={handleNumberChange} />
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <div className="border-t my-0" />
                  <AccordionItem value="panel-role">
                    <AccordionTrigger className="text-left font-semibold px-0 py-4 hover:bg-transparent focus:bg-transparent focus:outline-none">
                      Panel & Role
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="panelType">Panel Type</Label>
                          <Select name="panelType" value={umpire.panelType || ''} onValueChange={val => setUmpire(prev => ({ ...prev, panelType: val }))}>
                            <SelectTrigger id="panelType">
                              <SelectValue placeholder="Select panel type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Elite">Elite</SelectItem>
                              <SelectItem value="International">International</SelectItem>
                              <SelectItem value="National">National</SelectItem>
                              <SelectItem value="Domestic">Domestic</SelectItem>
                              <SelectItem value="Development">Development</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="primaryRole">Primary Role</Label>
                          <Select name="primaryRole" value={umpire.primaryRole || ''} onValueChange={val => setUmpire(prev => ({ ...prev, primaryRole: val }))}>
                            <SelectTrigger id="primaryRole">
                              <SelectValue placeholder="Select primary role" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="On-field Umpire">On-field Umpire</SelectItem>
                              <SelectItem value="Third Umpire (TV)">Third Umpire (TV)</SelectItem>
                              <SelectItem value="Fourth Umpire">Fourth Umpire</SelectItem>
                              <SelectItem value="Match Referee">Match Referee</SelectItem>
                              <SelectItem value="Reserve Umpire">Reserve Umpire</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <div className="border-t my-0" />
                  <AccordionItem value="experience">
                    <AccordionTrigger className="text-left font-semibold px-0 py-4 hover:bg-transparent focus:bg-transparent focus:outline-none">
                      Experience
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="matchesTest">Test Matches</Label>
                          <Input id="matchesTest" name="matchesTest" type="number" value={umpire.matchesTest ?? ''} onChange={handleNumberChange} />
                        </div>
                        <div>
                          <Label htmlFor="matchesOdi">ODI Matches</Label>
                          <Input id="matchesOdi" name="matchesOdi" type="number" value={umpire.matchesOdi ?? ''} onChange={handleNumberChange} />
                        </div>
                        <div>
                          <Label htmlFor="matchesT20i">T20I Matches</Label>
                          <Input id="matchesT20i" name="matchesT20i" type="number" value={umpire.matchesT20i ?? ''} onChange={handleNumberChange} />
                        </div>
                        <div>
                          <Label htmlFor="matchesFirstClass">First-Class Matches</Label>
                          <Input id="matchesFirstClass" name="matchesFirstClass" type="number" value={umpire.matchesFirstClass ?? ''} onChange={handleNumberChange} />
                        </div>
                        <div>
                          <Label htmlFor="matchesListA">List A Matches</Label>
                          <Input id="matchesListA" name="matchesListA" type="number" value={umpire.matchesListA ?? ''} onChange={handleNumberChange} />
                        </div>
                        <div>
                          <Label htmlFor="matchesT20Domestic">T20 Domestic Matches</Label>
                          <Input id="matchesT20Domestic" name="matchesT20Domestic" type="number" value={umpire.matchesT20Domestic ?? ''} onChange={handleNumberChange} />
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <div className="border-t my-0" />
                  <AccordionItem value="drs">
                    <AccordionTrigger className="text-left font-semibold px-0 py-4 hover:bg-transparent focus:bg-transparent focus:outline-none">
                      DRS
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="drsDecisionsMade">DRS Decisions Made</Label>
                          <Input id="drsDecisionsMade" name="drsDecisionsMade" type="number" value={umpire.drsDecisionsMade ?? ''} onChange={handleNumberChange} />
                        </div>
                        <div>
                          <Label htmlFor="drsDecisionsUpheld">DRS Decisions Upheld</Label>
                          <Input id="drsDecisionsUpheld" name="drsDecisionsUpheld" type="number" value={umpire.drsDecisionsUpheld ?? ''} onChange={handleNumberChange} />
                        </div>
                        <div>
                          <Label htmlFor="drsAccuracyPercent">DRS Accuracy (%)</Label>
                          <Input id="drsAccuracyPercent" name="drsAccuracyPercent" type="number" value={umpire.drsAccuracyPercent ?? ''} onChange={handleNumberChange} />
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <div className="border-t my-0" />
                  <AccordionItem value="other-details">
                    <AccordionTrigger className="text-left font-semibold px-0 py-4 hover:bg-transparent focus:bg-transparent focus:outline-none">
                      Other Details
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="languagesSpoken">Languages Spoken</Label>
                          <Input id="languagesSpoken" name="languagesSpoken" value={umpire.languagesSpoken || ''} onChange={handleChange} />
                        </div>
                        <div>
                          <Label htmlFor="certifications">Certifications</Label>
                          <Input id="certifications" name="certifications" value={umpire.certifications || ''} onChange={handleChange} />
                        </div>
                        <div className="md:col-span-2">
                          <Label htmlFor="remarks">Remarks</Label>
                          <Input id="remarks" name="remarks" value={umpire.remarks || ''} onChange={handleChange} />
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
                <div className="flex justify-end">
                  <Button type="submit" disabled={isSubmitting} className="bg-cricket-green">
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditUmpireModal; 